// components/landing/ControlPanelSection.tsx
'use client';

import React, { useState } from 'react';
import { Gauge, Droplets, Sun, Wind, Power, Settings, ChevronRight, Activity } from 'lucide-react';

interface ControlDevice {
  id: string;
  name: string;
  icon: React.ElementType;
  status: 'on' | 'off' | 'auto';
  value: string;
  color: string;
}

export default function ControlPanelSection() {
  const [devices, setDevices] = useState<ControlDevice[]>([
    {
      id: '1',
      name: '관수 시스템',
      icon: Droplets,
      status: 'auto',
      value: '2.5 L/h',
      color: 'blue',
    },
    {
      id: '2',
      name: 'LED 조명',
      icon: Sun,
      status: 'on',
      value: '80%',
      color: 'yellow',
    },
    {
      id: '3',
      name: '환기 시스템',
      icon: Wind,
      status: 'auto',
      value: '정상',
      color: 'cyan',
    },
    {
      id: '4',
      name: '온도 조절',
      icon: Gauge,
      status: 'on',
      value: '24.5°C',
      color: 'orange',
    },
  ]);

  const toggleDevice = (id: string) => {
    setDevices(
      devices.map((device) => {
        if (device.id === id) {
          const newStatus = device.status === 'on' ? 'off' : device.status === 'off' ? 'auto' : 'on';
          return { ...device, status: newStatus };
        }
        return device;
      })
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'on':
        return 'bg-green-500';
      case 'off':
        return 'bg-gray-400';
      case 'auto':
        return 'bg-blue-500';
      default:
        return 'bg-gray-400';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'on':
        return '작동중';
      case 'off':
        return '정지';
      case 'auto':
        return '자동';
      default:
        return '알수없음';
    }
  };

  return (
    <section className="relative bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-blue-900/20 overflow-hidden py-24 lg:py-32">
      {/* 배경 패턴 */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* 왼쪽: 컨트롤 패널 데모 */}
          <div className="order-2 lg:order-1">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8">
              {/* 헤더 */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500 rounded-lg">
                    <Settings className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">제어 패널</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">실시간 장치 제어</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-green-500 animate-pulse" />
                  <span className="text-sm text-green-500 font-medium">연결됨</span>
                </div>
              </div>

              {/* 장치 목록 */}
              <div className="space-y-3">
                {devices.map((device) => {
                  const Icon = device.icon;
                  return (
                    <div
                      key={device.id}
                      className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:shadow-md transition-all duration-200"
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <div className={`p-3 bg-${device.color}-100 dark:bg-${device.color}-900/30 rounded-lg`}>
                          <Icon className={`w-5 h-5 text-${device.color}-600 dark:text-${device.color}-400`} />
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900 dark:text-white">{device.name}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">{device.value}</div>
                        </div>
                      </div>
                      <button
                        onClick={() => toggleDevice(device.id)}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white dark:bg-gray-600 border border-gray-200 dark:border-gray-500 hover:border-blue-500 dark:hover:border-blue-400 transition-all"
                      >
                        <div className={`w-2 h-2 rounded-full ${getStatusColor(device.status)} animate-pulse`}></div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{getStatusText(device.status)}</span>
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* 하단 버튼 */}
              <button className="w-full mt-6 flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-all duration-200">
                전체 설정 보기
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* 오른쪽: 설명 */}
          <div className="order-1 lg:order-2 text-center lg:text-left">
            <div className="inline-flex items-center px-4 py-2 bg-blue-100 dark:bg-blue-900/30 rounded-full text-blue-700 dark:text-blue-300 text-sm font-medium mb-6">
              <Power className="w-4 h-4 mr-2" />
              스마트 제어
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              모든 장치를
              <span className="block text-blue-600 dark:text-blue-400 mt-2">한곳에서 제어</span>
            </h2>

            <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
              관수, 조명, 환기, 온도 조절 등 모든 농장 장치를 실시간으로 모니터링하고 제어하세요. 자동 모드로 설정하면 AI가 최적의 환경을
              유지합니다.
            </p>

            {/* 기능 리스트 */}
            <div className="space-y-4">
              {['원격 제어 및 모니터링', '자동 스케줄링', '실시간 알림', 'AI 기반 최적화'].map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                    <ChevronRight className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-gray-700 dark:text-gray-300 font-medium">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
